import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAtomValue } from "jotai";

import {
  expressionMasterDataCacheAtom,
  interpreterStoreAtom,
  runtimeApplicationAtom,
  selectedApplicantAtom,
} from "@/store";
import {
  normalizeTargetUrlAttribute,
  type TargetUrlAttributeValue,
} from "../../attributes/target-url/definition";
import { validateExpressionActionUrl } from "@/utils/action-url";
import { useRuntimeReferenceEvaluator } from "@/hooks/useCode/useRuntimeReferenceEvaluator";
import { extractReferencedMasterDataTableNames } from "@/hooks/useCode/utils";
import { classifyExpressionDependencies } from "@/hooks/useCode/expressionDependencies";
import { useDisableExpressionExecution } from "../../expression-execution-context";

type UseResolvedTargetUrlOptions = {
  disabled?: boolean;
};

type ResolveTargetUrlOptions = {
  force?: boolean;
};

export function useResolvedTargetUrl(
  targetUrl: TargetUrlAttributeValue,
  options: UseResolvedTargetUrlOptions = {},
) {
  const disableExpressionExecution = useDisableExpressionExecution();
  const interpreterStore = useAtomValue(interpreterStoreAtom);
  const expressionMasterDataCache = useAtomValue(expressionMasterDataCacheAtom);
  const selectedApplicant = useAtomValue(selectedApplicantAtom);
  const runtimeApplication = useAtomValue(runtimeApplicationAtom);
  const { evaluateReference } = useRuntimeReferenceEvaluator();
  const isResolutionDisabled =
    disableExpressionExecution || options.disabled === true;
  const normalized = useMemo(
    () => normalizeTargetUrlAttribute(targetUrl),
    [targetUrl],
  );
  const expression = normalized.expression?.trim() ?? "";
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  const requestIdRef = useRef(0);
  const previousContextRef = useRef({ runtimeApplication, selectedApplicant });

  const masterDataTableKeys = useMemo(
    () => extractReferencedMasterDataTableNames(expression),
    [expression],
  );

  const dependencyConfig = useMemo(() => {
    if (!interpreterStore || normalized.mode !== "expression") {
      return null;
    }
    return classifyExpressionDependencies(
      expression,
      interpreterStore.schema.entities,
    );
  }, [expression, interpreterStore, normalized.mode]);

  const canEvaluateMasterData = useMemo(() => {
    return !masterDataTableKeys.some((tableKey) => {
      const status = expressionMasterDataCache[tableKey]?.status;
      return !status || status === "idle" || status === "loading";
    });
  }, [expressionMasterDataCache, masterDataTableKeys]);

  const resolveExpressionUrl = useCallback(
    async (
      resolveOptions: ResolveTargetUrlOptions = {},
    ): Promise<string | undefined> => {
      if (isResolutionDisabled && !resolveOptions.force) {
        setResolvedUrl(null);
        setErrorKey(null);
        setIsResolving(false);
        return undefined;
      }

      if (normalized.mode !== "expression") {
        setResolvedUrl(null);
        setErrorKey(null);
        setIsResolving(false);
        return undefined;
      }

      if (!expression) {
        setResolvedUrl(null);
        setErrorKey("action_url.empty_expression");
        setIsResolving(false);
        return undefined;
      }

      if (!canEvaluateMasterData) {
        return undefined;
      }

      const requestId = ++requestIdRef.current;
      setIsResolving(true);
      try {
        const result = await evaluateReference(expression);
        if (requestId !== requestIdRef.current) {
          return;
        }
        const validation = validateExpressionActionUrl(result);
        if (!validation.isValid) {
          setResolvedUrl(null);
          setErrorKey(validation.errorKey);
          return undefined;
        }
        setResolvedUrl(validation.url);
        setErrorKey(null);
        return validation.url;
      } catch (error) {
        if (requestId !== requestIdRef.current) {
          return undefined;
        }
        console.warn("Failed to resolve action button URL expression", error);
        setResolvedUrl(null);
        setErrorKey("action_url.evaluate_failed");
        return undefined;
      } finally {
        if (requestId === requestIdRef.current) {
          setIsResolving(false);
        }
      }
    },
    [
      canEvaluateMasterData,
      evaluateReference,
      expression,
      isResolutionDisabled,
      normalized.mode,
    ],
  );

  const resolveTargetUrl = useCallback(
    async (
      resolveOptions: ResolveTargetUrlOptions = {},
    ): Promise<string | undefined> => {
      if (normalized.mode === "fixed") {
        return normalized.value;
      }

      return resolveExpressionUrl(resolveOptions);
    },
    [normalized.mode, normalized.value, resolveExpressionUrl],
  );

  useEffect(() => {
    if (isResolutionDisabled) {
      setResolvedUrl(null);
      setErrorKey(null);
      setIsResolving(false);
      return;
    }

    if (normalized.mode !== "expression") {
      setResolvedUrl(null);
      setErrorKey(null);
      setIsResolving(false);
      return;
    }
    const timeoutId = setTimeout(() => void resolveExpressionUrl(), 0);
    return () => clearTimeout(timeoutId);
  }, [isResolutionDisabled, normalized.mode, resolveExpressionUrl]);

  useEffect(() => {
    if (
      isResolutionDisabled ||
      !interpreterStore ||
      normalized.mode !== "expression"
    ) {
      return;
    }

    const unsubscribe = interpreterStore.subscribe(
      (_data: unknown, events: any[]) => {
        const updatedEntityIds = events
          .filter((event) => event?.name === "EntityValueUpdated")
          .map((event) => event?.payload?.entityId)
          .filter((id): id is string => typeof id === "string");

        if (updatedEntityIds.length === 0) {
          return;
        }

        const shouldResolve =
          dependencyConfig?.mode === "broad" ||
          (dependencyConfig?.mode === "scoped" &&
            updatedEntityIds.some((id) =>
              dependencyConfig.dependencyIds.includes(id),
            ));

        if (!shouldResolve) {
          return;
        }

        setTimeout(() => void resolveExpressionUrl(), 0);
      },
    );

    return unsubscribe;
  }, [
    dependencyConfig?.dependencyIds,
    dependencyConfig?.mode,
    interpreterStore,
    isResolutionDisabled,
    normalized.mode,
    resolveExpressionUrl,
  ]);

  useEffect(() => {
    if (isResolutionDisabled) {
      previousContextRef.current = { runtimeApplication, selectedApplicant };
      return;
    }

    if (normalized.mode !== "expression") {
      previousContextRef.current = { runtimeApplication, selectedApplicant };
      return;
    }
    const previousContext = previousContextRef.current;
    previousContextRef.current = { runtimeApplication, selectedApplicant };
    if (
      previousContext.runtimeApplication === runtimeApplication &&
      previousContext.selectedApplicant === selectedApplicant
    ) {
      return;
    }
    void resolveExpressionUrl();
  }, [
    isResolutionDisabled,
    normalized.mode,
    resolveExpressionUrl,
    runtimeApplication,
    selectedApplicant,
  ]);

  return {
    mode: normalized.mode,
    fixedUrl: normalized.value,
    resolvedUrl,
    resolveTargetUrl,
    errorKey,
    isResolving,
  };
}
